import * as React from "react";
import { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { BorrowerNotificationType } from "@tlc/ls2pac/dto/BorrowerNotificationType";

export interface SMSGateway {
  active: boolean;
  carrierName: string;
  emailSuffix: string;
  id: number;
}

export enum ObrDataFormatting {
  UpperCase = "UpperCase",
  MixedCase = "MixedCase",
  AsIs = "AsIs",
}

export enum ObrPinGeneration {
  Last4OfPhone = "Last4OfPhone",
  Birthday = "Birthday",
  None = "None",
}

export enum ObrPhoneFormatting {
  Traditional = "Traditional",
  Hyphenated = "Hyphenated",
  Dotted = "Dotted",
  Numeric = "Numeric",
}

export enum ObrFormFieldUsage {
  Mandatory = "Mandatory",
  Required = "Required",
  Shown = "Shown",
  Hidden = "Hidden",
}

export interface ObrFormField {
  fieldName: string;
  obrFormFieldUsage: ObrFormFieldUsage;
  displayName: string;
  checkForDuplicates: boolean;
  checkForDuplicatesConfigurable: boolean;
  mandatory: boolean;
  required: boolean;
  visible: boolean;
}

export enum BorrowerFieldInputType {
  TextBox = "TextBox",
  DropDown = "DropDown",
}
export interface BorrowerFieldOption {
  userValue: string;
  description: string;
}

export interface BorrowerField {
  fieldNumber: number;
  description: string;
  defaultValue: string;
  inputType: BorrowerFieldInputType;
  obrUsage: ObrFormFieldUsage;
  dropDownOptions: BorrowerFieldOption[];
}

export interface ObrConfiguration {
  obrEnabled: boolean;
  defaultObrBarcodePrefix: string;
  defaultObrCardExpirationDays: number;
  sendEmailConfirmation: boolean;
  useJcapCaptchas: boolean;
  dataFormatting: ObrDataFormatting;
  phoneFormatting: ObrPhoneFormatting;
  howToGeneratePin: ObrPinGeneration;
  obrFormFields: ObrFormField[];
  borrowerFields: BorrowerField[];
  generalMessage: Record<string, string>;
  termsAndConditions: Record<string, string>;
  borrowerTypesWhoCanAddDependents: string[];
  locationsToExclude: number[];
  pinLengthRequired: number;
}

export interface ObrPageData {
  obrConfiguration: ObrConfiguration;
  smsGateways: SMSGateway[];
  borrowerNotificationTypesAvailable: {
    arrivedHoldNotificationTypes: BorrowerNotificationType[];
    automaticRenewalNotificationTypes: BorrowerNotificationType[];
    cancelledHoldNotificationTypes: BorrowerNotificationType[];
    expiredCardNotificationTypes: BorrowerNotificationType[];
    finalOverdueNotificationTypes: BorrowerNotificationType[];
    friendlyWarningNotificationTypes: BorrowerNotificationType[];
    overdueNotificationTypes: BorrowerNotificationType[];
  };
}

const OBRSettingsContext = createContext<ObrPageData>(null);

export const OBRSettingHolder = (() => {
  let config: ObrPageData;

  return {
    getConfig: async () => {
      if (config) {
        return config;
      }
      try {
        const { data } = await axios.get<ObrPageData>("obr/config");
        config = data;
        return data;
      } catch (error) {
        console.error("Error fetching OBR Settings:", error);
      }
    },
  };
})();

export const OBRSettingsProvider = ({ children }: React.PropsWithChildren) => {
  const [obrConfiguration, setOBRConfiguration] = useState<ObrPageData>({
    obrConfiguration: null,
    smsGateways: null,
    borrowerNotificationTypesAvailable: null,
  });

  useEffect(() => {
    OBRSettingHolder.getConfig().then((data) => setOBRConfiguration(data));
  }, []);

  return (
    <OBRSettingsContext.Provider value={obrConfiguration}>
      {children}
    </OBRSettingsContext.Provider>
  );
};

export function useOBRSettings() {
  const context = useContext(OBRSettingsContext);

  if (!context) {
    throw new Error("useOBRSettings must be used within a OBRSettingsProvider");
  }

  return context;
}
